// Theme tokens — operator UI, orange accent, dense layout.
//
// Theme palette uses CSS custom properties so dark/light switching is a
// single attribute flip on <html>. Inline styles that read `G.bg` produce
// `style="background: var(--g-bg)"` and the browser resolves dynamically.
// applyTheme() (below) writes the palette to :root.

// Accent constants used only as fallbacks (e.g., generated style strings that
// need a static hex). Live values flow through CSS vars below.
const accentBorder = 'rgba(255,106,61,0.4)';

const THEMES = {
  dark: {
    bg: '#0e0f12', sidebar: '#13151a', card: '#161922', cardBorder: '#23262f',
    input: '#1a1d24',
    text: '#e8e6e1', textMuted: '#8a8c96', textDim: '#4a4d56', border: '#1f2128',
    // *Light tints — barely-there on dark bg, work as subtle highlights
    accentDim: 'rgba(255,106,61,0.14)',
    blueLight:   'rgba(59,130,246,0.13)',
    cyanLight:   'rgba(6,182,212,0.13)',
    greenLight:  'rgba(34,197,94,0.13)',
    yellowLight: 'rgba(234,179,8,0.13)',
    redLight:    'rgba(239,68,68,0.13)',
    // Accents — bright shades that pop on dark bg
    accent: '#ff6a3d',
    blue:   '#3b82f6',
    cyan:   '#06b6d4',
    green:  '#22c55e',
    yellow: '#eab308',
    red:    '#ef4444',
    pink:   '#ec4899',
  },
  light: {
    bg: '#f5f6fa', sidebar: '#ffffff', card: '#ffffff', cardBorder: '#d6dae3',
    input: '#eef0f6',
    // Light mode: ALL text is dead black. Per direct feedback, anything other
    // than #000 still reads as faint at small font sizes on owners' high-DPI
    // displays. Borders stay light gray so the layout still feels airy.
    text: '#000000', textMuted: '#000000', textDim: '#000000', border: '#d6dae3',
    // *Light tints — much stronger opacity so badges + pills are visible
    // against the white card background. The dark theme keeps its airy look.
    accentDim: 'rgba(255,106,61,0.18)',
    blueLight:   'rgba(59,130,246,0.18)',
    cyanLight:   'rgba(6,182,212,0.18)',
    greenLight:  'rgba(34,197,94,0.20)',
    yellowLight: 'rgba(234,179,8,0.22)',
    redLight:    'rgba(239,68,68,0.18)',
    // Accents — DEEPER shades for WCAG-AA contrast on white. The bright
    // dark-mode accents (#ff6a3d, #06b6d4, etc.) wash out on white and make
    // labels like "TRADEOPS COACH" or "ONE-CLICK ACTIONS" hard to read.
    accent: '#c2410c',   // burnt orange
    blue:   '#1d4ed8',
    cyan:   '#0e7490',
    green:  '#15803d',
    yellow: '#a16207',   // dark amber — yellow on white is unreadable
    red:    '#b91c1c',
    pink:   '#be185d',
  },
};

export function applyTheme(name) {
  if (typeof document === 'undefined') return;
  const t = THEMES[name] || THEMES.dark;
  const root = document.documentElement;
  for (const [k, v] of Object.entries(t)) root.style.setProperty(`--g-${k}`, v);
  root.setAttribute('data-theme', name);
  try { localStorage.setItem('tradeops_theme', name); } catch {}
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', t.bg);
}

export function getTheme() {
  if (typeof document === 'undefined') return 'dark';
  return document.documentElement.getAttribute('data-theme') || 'dark';
}

// Apply persisted theme immediately on module import to avoid a flash.
if (typeof document !== 'undefined') {
  let saved = null;
  try { saved = localStorage.getItem('tradeops_theme'); } catch {}
  applyTheme(saved === 'light' ? 'light' : 'dark');
}

export const G = {
  // Theme-dependent — resolve to CSS vars
  bg: 'var(--g-bg)', sidebar: 'var(--g-sidebar)', card: 'var(--g-card)', cardBorder: 'var(--g-cardBorder)',
  input: 'var(--g-input)',
  // Accents are now theme-dependent too — bright in dark mode, deeper in
  // light mode so labels stay readable on white card backgrounds.
  accent:       'var(--g-accent)',
  accentBorder,
  accentDim:    'var(--g-accentDim)',
  purple:       'var(--g-accent)',
  purpleBorder: accentBorder,
  purpleDim:    'var(--g-accentDim)',
  orange:       'var(--g-accent)',
  orangeLight:  'var(--g-accentDim)',
  blue:         'var(--g-blue)',
  blueLight:    'var(--g-blueLight)',
  cyan:         'var(--g-cyan)',
  cyanLight:    'var(--g-cyanLight)',
  green:        'var(--g-green)',
  greenLight:   'var(--g-greenLight)',
  yellow:       'var(--g-yellow)',
  yellowLight:  'var(--g-yellowLight)',
  red:          'var(--g-red)',
  redLight:     'var(--g-redLight)',
  pink:         'var(--g-pink)',
  // Theme-dependent — resolve to CSS vars
  text: 'var(--g-text)', textMuted: 'var(--g-textMuted)', textDim: 'var(--g-textDim)', border: 'var(--g-border)',
};

// Dense layout tokens — tighter than v1 so a full dashboard fits without
// scrolling. 13–14px text, 8–14px padding, pill badges, card-table pattern.
export const styles = {
  card: { background: G.card, border: `1px solid ${G.cardBorder}`, borderRadius: 10, padding: '14px 16px' },
  inp: { background: G.input, border: `1px solid ${G.cardBorder}`, borderRadius: 7, padding: '8px 12px', color: G.text, fontSize: 13, outline: 'none', width: '100%' },
  th: { textAlign: 'left', fontSize: 11, color: G.textMuted, fontWeight: 700, padding: '9px 12px', borderBottom: `1px solid ${G.border}`, textTransform: 'uppercase', letterSpacing: '0.05em' },
  td: { padding: '10px 12px', borderBottom: `1px solid ${G.cardBorder}55`, fontSize: 13 },
  ovl: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 },
};

export function api(path, opts = {}) {
  const base = (typeof window !== 'undefined' && window.__API_BASE__) || '';
  return fetch(`${base}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(opts.headers || {}) },
    ...opts,
    body: opts.body && typeof opts.body !== 'string' ? JSON.stringify(opts.body) : opts.body,
  }).then(async r => {
    const ct = r.headers.get('content-type') || '';
    const data = ct.includes('application/json') ? await r.json() : await r.text();
    if (!r.ok) {
      // Paywall: subscription required. Broadcast so the app can surface a "go to billing" banner.
      if (r.status === 402 && data?.error === 'subscription_required') {
        try { window.dispatchEvent(new CustomEvent('tradeops:paywall', { detail: data })); } catch {}
        const err = new Error(data.message || 'Subscription required');
        err.code = 'subscription_required';
        throw err;
      }
      // 401: session expired or logged-out. Broadcast so the shell can prompt
      // re-login instead of leaving the user on a screen full of stuck spinners.
      // Skip on /api/auth/user — that's the "am I logged in?" check itself.
      if (r.status === 401 && !path.startsWith('/api/auth/user')) {
        try { window.dispatchEvent(new CustomEvent('tradeops:auth-expired')); } catch {}
        const err = new Error('Session expired — please log in again');
        err.code = 'auth_expired';
        throw err;
      }
      // 403 "No account context": a super_admin is acting at agency level without
      // having entered a sub-account. Broadcast so the shell can show an in-app
      // toast with a link that does it, and throw a message that tells the user
      // what to do rather than surfacing the bare server string in a raw alert.
      if (r.status === 403 && data?.error === 'No account context') {
        try { window.dispatchEvent(new CustomEvent('tradeops:no-account-context')); } catch {}
        const err = new Error('Enter a sub-account first to do that');
        err.code = 'no_account_context';
        throw err;
      }
      throw new Error((data && data.error) || `HTTP ${r.status}`);
    }
    return data;
  });
}

export const fmtUsd = n => `$${(Number(n)||0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}`;

export function calcEst(lines, taxRate = 7) {
  const sub = (lines || []).reduce((s,l)=>s + (Number(l.qty)||0) * (Number(l.price)||0), 0);
  const tax = sub * (Number(taxRate)/100);
  return { sub, tax, total: sub + tax };
}
